import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Token } from '../core/models/token.model';
import { UserInfo } from '../core/models/user-info.model';
import { PASS_ERROR_HEADER } from '../core/consts/api.const';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AUTH_PROVIDER_TOKEN, AuthProvider } from '../core/tokens/auth-provider.token';
import { MsalService } from '@azure/msal-angular';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private authUrl = '/auth-api/authentication';
  private loginUrl = `${this.authUrl}/login`;
  private registerUrl = `${this.authUrl}/register`;
  private refreshTokenUrl = `${this.authUrl}/refreshToken`;

  constructor(
    private httpClient: HttpClient,
    @Inject(AUTH_PROVIDER_TOKEN) private authProvider: AuthProvider,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private msalService: MsalService,
  ) {}

  login(email: string, password: string): Observable<Token> {
    return this.httpClient.post<Token>(this.loginUrl, { email, password });
  }

  register(email: string, password: string): Observable<null> {
    return this.httpClient.post<null>(this.registerUrl, { email, password });
  }

  refresh(accessToken: string, refreshToken: string): Observable<Token> {
    return this.httpClient.post<Token>(
      this.refreshTokenUrl,
      { accessToken, refreshToken },
      {
        headers: new HttpHeaders().set(PASS_ERROR_HEADER, 'true'),
      },
    );
  }

  getCurrentUser(): Observable<UserInfo> {
    if (this.authProvider === 'Entra') {
      const account =
        this.msalService.instance.getActiveAccount() ??
        this.msalService.instance.getAllAccounts()[0];
      return of({
        email: account?.username ?? account?.name ?? '',
      });
    }

    if (this.authProvider === 'Okta') {
      return from(this.oktaAuth.getUser()).pipe(
        map((user: { email?: string }) => ({
          email: user.email || '',
        })),
      );
    }

    return this.httpClient.get<UserInfo>(`${this.authUrl}/currentUser`);
  }
}
